import { useCallback, useContext, useEffect, useRef } from "react";
import { AuthContext } from "../context/AuthContext";
import { refreshAuthSession } from "../utils/sessionRefresh";

const REFRESH_INTERVAL_MS = 15_000;

/**
 * Keeps sessionStorage permissions/roles in sync with the server while logged in.
 * Polls every 30s and refreshes immediately when the tab becomes visible again.
 */
export function useSessionRefresh() {
    const { isAuthenticated, bumpAuthRevision } = useContext(AuthContext);
    const inFlightRef = useRef(false);

    const runRefresh = useCallback(async () => {
        if (!isAuthenticated || inFlightRef.current) return;
        inFlightRef.current = true;
        try {
            const result = await refreshAuthSession();
            if (result.ok && (result.permissionsChanged || result.groupChanged)) {
                bumpAuthRevision?.();
            }
        } finally {
            inFlightRef.current = false;
        }
    }, [isAuthenticated, bumpAuthRevision]);

    useEffect(() => {
        if (!isAuthenticated) return undefined;

        runRefresh();

        const intervalId = window.setInterval(runRefresh, REFRESH_INTERVAL_MS);

        const onVisible = () => {
            if (document.visibilityState === "visible") {
                runRefresh();
            }
        };
        document.addEventListener("visibilitychange", onVisible);
        window.addEventListener("focus", runRefresh);

        return () => {
            window.clearInterval(intervalId);
            document.removeEventListener("visibilitychange", onVisible);
            window.removeEventListener("focus", runRefresh);
        };
    }, [isAuthenticated, runRefresh]);
}
